// Most types copied from legacy/**/dco
import { fetchWithTimeout } from '@web-rendering/groundcontrol-utils/fetch-with-timeout';
import { setNativeAssets } from './signal';
import type { RawNativeAssets } from './types/assets';

export type DcoDataAsset = {
  type: number;
  value: string;
};

export type DcoVideoAsset = {
  vasttag: string;
};

export type DcoLinkAsset = {
  url: string;
  clicktrackers: string[];
};

export type DcoImageAsset = {
  type: number;
  url: string;
  h: number;
  w: number;
};

export type DcoTitleAsset = {
  text: string;
};

export type DcoEventTracker = {
  url: string;
  event: number;
  method: number;
};

export type DcoAsset = {
  title?: DcoTitleAsset;
  img?: DcoImageAsset;
  data?: DcoDataAsset;
  video?: DcoVideoAsset;
  link?: DcoLinkAsset;
};

export type DcoAssetResponse = {
  assets: DcoAsset[];
  eventtrackers: DcoEventTracker[];
};

export const DCO_REQUEST_TIMEOUT = 1200;

export const handleJsonp = async (response: Response) => {
  const responseText = await response.text();
  const jsonpMatch = responseText.match(/{.*}/);
  if (jsonpMatch) {
    return new Response(jsonpMatch[0], {
      status: 200,
      statusText: 'OK',
      headers: response.headers
    });
  }

  return response;
};

export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'Unknown error';
};

export const isResponseJsonp = (response: Response) => {
  return response.headers.get('content-type') === 'application/javascript';
};

export async function parseDcoResponseFromPayload(url: string): Promise<DcoAsset[] | null> {
  /** 
   * Comment out until we decide to fire beacon or not 
   *   if (!url) {
   *     DcoHandler.fireBeacon(
   *      {
   *        result: DcoStatus.Failure,
   *        name: 'No asset URL'
   *      },
   *        payload
   *      );
   *
   *      return null;
   *    }

   */

  try {
    let response: Response;
    try {
      response = await fetchWithTimeout(url, {
        timeout: DCO_REQUEST_TIMEOUT
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      throw new Error(`DCO Request Failed : ${message}`);
    }

    if (isResponseJsonp(response)) {
      response = await handleJsonp(response);
    }

    const responseText = await response.text();
    if (responseText.trim().length === 0) {
      return null;
    }

    let assetResponse: DcoAssetResponse;
    try {
      assetResponse = JSON.parse(responseText);
    } catch (error) {
      throw new Error('Invalid JSON response');
    }

    if (Object.keys(assetResponse).length === 0) {
      return null;
    }

    return assetResponse.assets;
  } catch (error) {
    const message = getErrorMessage(error);
    console.error('Failed to parse Dco assets', message);
    /**
     * Comment out until we decide to fire beacon or not
     *     const result = message.includes('timeout') ? DcoStatus.RequestTimeout : DcoStatus.Failure;
     *
     *      DcoHandler.fireBeacon(
     *         {
     *           result,
     *           name: error instanceof Error ? error.message : error
     *         },
     *           payload
     *       );
     */

    throw error;
  }
}

export function replaceNativeAssetWithDco(url: string, nativeAssets: RawNativeAssets): void {
  parseDcoResponseFromPayload(url)
    .then((dcoNativeAssets) => {
      if (dcoNativeAssets) {
        setNativeAssets(dcoNativeAssets);
      }
    })
    .catch((_) => {
      setNativeAssets(nativeAssets);
    });
}
